import React, { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { getProjectLogoUrl } from '../../services/api';

interface LogoDisplayProps {
  projectId: string;
  logoPath?: string;
  projectName: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showFallback?: boolean;
}

export const LogoDisplay: React.FC<LogoDisplayProps> = ({
  projectId,
  logoPath,
  projectName,
  className = '',
  size = 'medium',
  showFallback = true
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const sizeClasses = {
    small: 'h-8 max-w-32',
    medium: 'h-12 max-w-48',
    large: 'h-16 max-w-64'
  };

  const placeholderSizeClasses = {
    small: 'h-8 w-8',
    medium: 'h-12 w-12',
    large: 'h-16 w-16'
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Fetch logo URL from API
  useEffect(() => {
    const fetchLogoUrl = async () => {
      try {
        if (!logoPath) {
          setLogoUrl(null);
          setLoading(false);
          return;
        }

        const response = await getProjectLogoUrl(projectId);
        if (response.data.success && response.data.data.hasLogo) {
          setLogoUrl(response.data.data.logoUrl);
        } else {
          setLogoUrl(null);
        }
      } catch (error) {
        console.error('Error fetching logo URL:', error);
        setLogoUrl(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLogoUrl();
  }, [projectId, logoPath]);

  // Show loading state
  if (loading) {
    return (
      <div className={`corporate-logo-container ${className}`}>
        <div className={`corporate-logo-placeholder ${placeholderSizeClasses[size]} animate-pulse`}>
          <Building2 className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    );
  }

  // Show logo if we have a valid URL and no error
  if (logoUrl && !imageError) {
    return (
      <div className={`corporate-logo-container ${className}`}>
        <img
          src={logoUrl}
          alt={`Logo ${projectName}`}
          className={`corporate-logo ${sizeClasses[size]} object-contain`}
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
        {!imageLoaded && (
          <div className={`corporate-logo-placeholder ${placeholderSizeClasses[size]} animate-pulse`}>
            <Building2 className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>
    );
  }

  // Show fallback if enabled and no logo available
  if (showFallback) {
    return (
      <div className={`corporate-logo-container ${className}`}>
        <div className={`corporate-logo-placeholder ${placeholderSizeClasses[size]}`}>
          <Building2 className="w-4 h-4 text-gray-400" />
        </div>
        <div className="corporate-logo-text">
          <span className="font-semibold text-corporate-text-primary">
            {projectName}
          </span>
        </div>
      </div>
    );
  }

  // Return null if no logo and no fallback
  return null;
};

export default LogoDisplay;